









import { computed, defineComponent, PropType, ref } from "@vue/composition-api";
import { ContentSet } from "@scrile/api-provider/dist/api/ContentSetProvider";
import { PaymentPackageEnum } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import { ContentFeedTypeEnum } from "@scrile/api-provider/dist/projects/scrileConnect/ContentFeedProvider";
import { EventPost, EventPostStatus } from "@scrile/api-provider/dist/projects/scrileConnect/EventPostProvider";
import { UserPublicData } from "@scrile/api-provider/dist/projects/scrileConnect/UserPublicDataProvider";
import usePaymentPackages from "@/hooks/usePaymentPackages";
import providers from "@/lib/api";
import { $t } from "@/lib/i18nHelpers";
import { formatMoneyString } from "@/lib/moneyHelpers";
import { format, parse } from "@/lib/time";
import { FormFields, FormValues } from "@/types";
import BaseForm from "@/components/BaseForm";
import BaseSpinner from "@/components/BaseSpinner";

interface EventDetailsFields {
  date: string;
  duration: number;
  levelId: string;
  pricePackageId: string;
  time: string;
}

export default defineComponent({
  name: "EventDetails",
  components: { BaseSpinner, BaseForm },
  props: {
    postData: { type: Object as PropType<ContentSet>, required: true },
  },
  setup(props, { emit }) {
    const { paymentPackages, getPaymentPackages } = usePaymentPackages([PaymentPackageEnum.FILESET]);
    const loading = ref(false);
    const eventData = ref<EventPost | null>(null);
    const pricePackageId = ref("0");
    const levelId = ref("0");
    const values = ref<FormValues>({
      date: format(new Date(), "yyyy-MM-dd"),
      time: format(new Date(), "HH:mm"),
      duration: 15,
      levelId: "0",
      pricePackageId: "0",
    });
    const fields = computed<FormFields>(() => {
      const packageOptions = [{ text: $t("Disable one-time purchase"), value: "0" }].concat(
        paymentPackages.value.map(packageData => ({
          text: formatMoneyString(packageData.amount),
          value: packageData.id,
        }))
      );

      return [
        {
          name: "date",
          label: $t("Date"),
          type: "date",
          rules: ["required"],
          disabled: props.postData.published,
          validationMode: "aggressive",
        },
        {
          name: "time",
          label: $t("Time"),
          type: "time",
          rules: ["required"],
          disabled: props.postData.published,
          validationMode: "aggressive",
        },
        {
          name: "duration",
          label: $t("Duration"),
          type: "select",
          rules: ["required"],
          disabled: props.postData.published,
          validationMode: "aggressive",
          options: [
            { text: "15 min", value: 15 },
            { text: "30 min", value: 30 },
            { text: "60 min", value: 60 },
            { text: "90 min", value: 90 },
            { text: "120 min", value: 120 },
          ],
        },
        {
          name: "pricePackageId",
          label: $t("Ticket price"),
          type: "select",
          rules: ["required"],
          disabled: props.postData.published,
          validationMode: "aggressive",
          options: packageOptions,
        },
      ];
    });

    const getEventTime = (date: string, time: string) => {
      return parse(`${date} ${time}`, "yyyy-MM-dd HH:mm", new Date()).toISOString();
    };

    const onInput = ({ date, duration, levelId, pricePackageId, time }: EventDetailsFields) => {
      values.value = { ...values.value, date, duration, time, levelId, pricePackageId };
      if (eventData.value) {
        eventData.value = {
          ...eventData.value,
          duration,
          time: getEventTime(date, time),
          minimumLevelId: levelId,
          ticketPricePackageId: pricePackageId,
        };
        emit("updateEventData", eventData.value);
      }
    };

    const sendEventData = async () => {
      if (props.postData.published) return;
      await providers.EventPostProvider.update({
        data: {
          postId: props.postData.id,
          time: getEventTime(values.value.date, values.value.time),
          duration: values.value.duration,
        },
      });
      await Promise.all([
        providers.ContentPriceProvider.update({
          data: {
            subject: {
              subjectId: props.postData.id,
              subjectType: ContentFeedTypeEnum.EVENT,
            },
            packageId: values.value.pricePackageId,
          },
        }),
        providers.ContentSubscriptionLevelProvider.update({
          data: {
            subject: {
              subjectId: props.postData.id,
              subjectType: ContentFeedTypeEnum.EVENT,
            },
            levelId: values.value.levelId,
          },
        }),
      ]);
    };

    const getEvent = async () => {
      const data = await providers.EventPostProvider.find({ postIds: [props.postData.id] });
      const event = data.find(i => i.postId === props.postData.id) ?? null;
      values.value = {
        date: format(new Date(eventData.value?.time ?? new Date()), "yyyy-MM-dd"),
        time: format(new Date(eventData.value?.time ?? new Date()), "HH:mm"),
        duration: event?.duration ?? 15,
        levelId: event?.minimumLevelId ?? levelId.value,
        pricePackageId: event?.ticketPricePackageId ?? pricePackageId.value,
      };

      eventData.value = {
        userId: event?.userId ?? props.postData.userId,
        user: event?.user ?? ({ id: props.postData.userId } as UserPublicData),
        postId: event?.postId ?? props.postData.id,
        time: getEventTime(values.value.date, values.value.time),
        duration: event?.duration ?? 15,
        ticketPricePackageId: event?.ticketPricePackageId ?? "0",
        ticketPricePackage: event?.ticketPricePackage ?? null,
        minimumLevelId: event?.minimumLevelId ?? "0",
        ticketsBooked: event?.ticketsBooked ?? 0,
        availableByTicket: event?.availableByTicket ?? false,
        status: event?.status ?? EventPostStatus.CREATED,
      };
      emit("updateEventData", eventData.value);
    };

    const getData = async () => {
      try {
        loading.value = true;
        await Promise.all([getEvent(), getPaymentPackages()]);
      } finally {
        loading.value = false;
      }
    };

    getData();
    emit("setUpdateCb", sendEventData);

    return {
      loading,
      values,
      fields,
      onInput,
    };
  },
});
